import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { monEspaceAPI } from '../api/monEspace.api';
import { PlanningData, PlanningMode, DEFAULT_PLANNING, DayAvailability } from '../types/monEspace.types';
import { toast } from 'react-hot-toast';

interface UseMonEspacePlanningProps {
  mode: PlanningMode;
  currentWeekStart: string;
  /**
   * When true, the current week is locked and cannot be edited. A week is
   * locked once it has been *validated* (its hours snapshotted into the recup
   * counter) — NOT merely because it is in the past. Past-but-unvalidated weeks
   * stay editable so the user can fix them before validating.
   */
  isLocked?: boolean;
}

/**
 * Hook for managing Mon Espace planning data
 * Handles both recurrent template and week-specific overrides
 */
export const useMonEspacePlanning = ({ mode, currentWeekStart, isLocked = false }: UseMonEspacePlanningProps) => {
  const queryClient = useQueryClient();

  // Query key based on mode
  const queryKey = mode === 'recurrent'
    ? ['monEspace', 'template']
    : ['monEspace', 'week', currentWeekStart];

  // Fetch planning data
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (mode === 'recurrent') {
        return await monEspaceAPI.getTemplate();
      } else {
        const response = await monEspaceAPI.getWeekPlanning(currentWeekStart);
        return response;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  });

  // Extract planning data based on mode
  const planning: PlanningData = mode === 'recurrent'
    ? (data as PlanningData) || DEFAULT_PLANNING
    : ((data as any)?.planning || DEFAULT_PLANNING);

  const hasOverrides = mode === 'week' ? (data as any)?.hasOverrides || false : false;
  const isPastWeek = mode === 'week' ? (data as any)?.isPastWeek || false : false;

  // Save template mutation
  const saveTemplateMutation = useMutation({
    mutationFn: (planningData: Record<string, DayAvailability>) => {
      return monEspaceAPI.saveTemplate(planningData);
    },
    onSuccess: () => {
      // Invalidate template cache
      queryClient.invalidateQueries({ queryKey: ['monEspace', 'template'] });
      // Invalidate ALL week caches since template changes affect all future weeks
      queryClient.invalidateQueries({ queryKey: ['monEspace', 'week'] });
      toast.success('Planning récurrent sauvegardé avec succès');
    },
    onError: (error: any) => {
      console.error('Error saving template:', error);
      toast.error('Erreur lors de la sauvegarde du planning récurrent');
    }
  });

  // Save week override mutation
  const saveWeekMutation = useMutation({
    mutationFn: (planningData: Record<string, DayAvailability>) => {
      return monEspaceAPI.saveWeekOverride(currentWeekStart, planningData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monEspace', 'week', currentWeekStart] });
      toast.success('Planning de la semaine sauvegardé avec succès');
    },
    onError: (error: any) => {
      console.error('Error saving week override:', error);
      toast.error('Erreur lors de la sauvegarde du planning de la semaine');
    }
  });

  // Delete week override mutation
  const deleteWeekOverrideMutation = useMutation({
    mutationFn: () => {
      return monEspaceAPI.deleteWeekOverride(currentWeekStart);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monEspace', 'week', currentWeekStart] });
      toast.success('Surcharge supprimée, planning récurrent restauré');
    },
    onError: (error: any) => {
      console.error('Error deleting week override:', error);
      toast.error('Erreur lors de la suppression de la surcharge');
    }
  });

  // Save function that dispatches to the correct mutation based on mode
  const save = async (planningData: Record<string, DayAvailability>) => {
    // Prevent saving validated (locked) weeks. Past-but-unvalidated weeks
    // remain editable so users can correct them before validating.
    if (mode === 'week' && isLocked) {
      toast.error('Cette semaine est validée — dévalidez-la pour la modifier');
      throw new Error('Cannot edit validated week');
    }

    if (mode === 'recurrent') {
      return saveTemplateMutation.mutateAsync(planningData);
    } else {
      return saveWeekMutation.mutateAsync(planningData);
    }
  };

  // Delete week override function
  const deleteWeekOverride = async () => {
    if (mode === 'week') {
      return deleteWeekOverrideMutation.mutateAsync();
    }
  };

  const isSaving = saveTemplateMutation.isPending || saveWeekMutation.isPending;
  const isDeleting = deleteWeekOverrideMutation.isPending;

  return {
    planning,
    hasOverrides,
    isPastWeek,
    isLoading,
    isSaving,
    isDeleting,
    error,
    save,
    deleteWeekOverride,
    refetch
  };
};
